import { X, Calendar, Clock, Users, Luggage, MapPin } from 'lucide-react';
import type { BookingRecord } from '../../../types';
import { formatBookingDate, formatBookingTime, formatEuro } from '../../../utils/format';
import { cn } from '../../../utils/cn';
import { bookingAssets } from '../../../constants/assets';
import { DASHBOARD_API_BASE_URL } from '../../../api/config/config';
type ViewBookingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingRecord | null;
};

export const ViewBookingModal = ({
  isOpen,
  onClose,
  booking,
}: ViewBookingModalProps) => {
  if (!isOpen || !booking) return null;

  const serviceString = booking.service.toLowerCase();
  const isHourly = serviceString.includes('hourly');
  const isTwoWay = Boolean(booking.returnTrip);

  const passengers = booking.passengers || 0;
  const luggage = booking.luggage || 0;

  const statusLabel = (() => {
    switch (booking.status || booking.trip) {
      case 'confirmed':
        return 'Confirmed';
      case 'pending':
        return 'Pending';
      case 'in_progress':
      case 'on-trip':
      case 'in-progress':
        return 'On Trip';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'incomplete':
        return 'Incomplete';
      default:
        return booking.status || booking.trip || '-';
    }
  })();

  const statusClass = (() => {
    switch (booking.status || booking.trip) {
      case 'confirmed':
      case 'completed':
        return 'border-[#719642] text-[#719642]';
      case 'pending':
        return 'border-yellow-500 text-yellow-600';
      case 'cancelled':
        return 'border-red-500 text-red-500';
      case 'in_progress':
      case 'on-trip':
      case 'in-progress':
        return 'border-blue-500 text-blue-500';
      default:
        return 'border-gray-400 text-gray-600';
    }
  })();
console.log('SERVICE IMAGE:', booking.serviceImage);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="relative w-full max-w-[700px] rounded-[16px] bg-[#FAFAFA] p-6 shadow-lg">
        {/* Header Section */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-host text-[20px] font-semibold text-black">
            Booking Details
          </h2>

          <button
            onClick={onClose}
            className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-black text-white transition-colors hover:bg-black/80"
          >
            <X size={16} />
          </button>
        </div>

        {/* Top Details Card */}
        <div className="mb-6 flex gap-4">
          <div className="h-[120px] w-[120px] shrink-0 overflow-hidden rounded-[12px]">
            <img
              src={`${DASHBOARD_API_BASE_URL}${booking.serviceImage}`}
              alt={booking.service}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="flex flex-1 flex-col justify-between py-1">
            <div className="flex items-start justify-between">
              <div className="flex flex-col">
                <span className="mb-1 font-host text-[14px] text-black">
                  {booking.reference}
                </span>

                <h3 className="font-host text-[20px] font-semibold text-black">
                  {booking.service}
                </h3>
              </div>

              <div className="flex flex-col items-end gap-2">
                <span
                  className={cn(
                    'rounded-full border px-3 py-1 font-host text-[14px] font-medium',
                    statusClass
                  )}
                >
                  {statusLabel}
                </span>
              </div>
            </div>

            <div className="flex items-end justify-between">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-4 font-host text-[14px] text-black">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={16} />
                    {formatBookingDate(booking.scheduledAt)}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Clock size={16} />
                    {formatBookingTime(booking.scheduledAt)}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 rounded-full bg-[#EEEEEE] px-3 py-1 font-host text-[13px] font-medium text-black">
                    <Users size={14} />
                    {passengers}
                  </div>

                  <div className="flex items-center gap-1.5 rounded-full bg-[#EEEEEE] px-3 py-1 font-host text-[13px] font-medium text-black">
                    <Luggage size={14} />
                    {luggage}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <span
                  className={cn(
                    'font-host text-[14px] font-medium',
                    booking.payment === 'paid'
                      ? 'text-[#719642]'
                      : 'text-red-500'
                  )}
                >
                  {booking.payment === 'paid' ? 'Paid' : 'Unpaid'}
                </span>

                <span className="font-host text-[20px] font-medium text-black">
                  {formatEuro(booking.amountEur)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Information Section */}
        <h3 className="mb-4 font-host text-[18px] font-semibold text-black">
          Booking Information
        </h3>

        <div className="flex gap-8">
          {/* Left Column (Timeline) */}
          <div className="flex-1">
            <div className="mb-6">
              <span className="inline-block rounded-full bg-[#3B5B8E] px-4 py-1.5 font-host text-[12px] font-semibold tracking-wide text-white">
                {isTwoWay ? 'TWO WAY' : 'ONE WAY'}
              </span>
            </div>

            <div className="relative ml-[9px] flex flex-col gap-6 border-l-2 border-[#D9D9D9] pl-3">
              {/* Pickup */}
              <div className="relative">
                <div className="absolute -left-[14px] top-1 h-[20px] w-[20px] rounded-full border-[4px] border-black bg-white" />

                <div className="flex flex-col gap-1">
                  <span className="mb-1 inline-block w-fit rounded-[6px] border border-[#719642] bg-white px-2 py-0.5 text-[12px] font-medium text-black shadow-sm">
                    Pickup
                  </span>

                  <span className="font-host text-[15px] text-[#555555]">
                    {booking.route.from || '-'}
                  </span>
                </div>
              </div>

              {/* Drop-off / Hourly */}
              <div className="relative">
                {isHourly ? (
                  <>
                    <div className="absolute -left-[14px] top-1 flex h-[20px] w-[20px] items-center justify-center rounded-full bg-white">
                      <Clock size={16} className="bg-white text-black" />
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="mb-1 inline-block w-fit rounded-[6px] border border-gray-300 bg-white px-2 py-0.5 text-[12px] font-medium text-black shadow-sm">
                        Duration
                      </span>

                      <span className="font-host text-[15px] text-[#555555]">
                        Not specified
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="absolute -left-[14px] top-1 flex h-[20px] w-[20px] items-center justify-center rounded-full border-[2px] border-black bg-white">
                      <MapPin size={12} className="text-black" />
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="mb-1 inline-block w-fit rounded-[6px] border border-red-300 bg-white px-2 py-0.5 text-[12px] font-medium text-black shadow-sm">
                        Drop-off
                      </span>

                      <span className="font-host text-[15px] text-[#555555]">
                        {booking.route.to || '-'}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Return Trip */}
              {isTwoWay && booking.returnDateTime && (
                <div className="relative">
                  <div className="absolute -left-[14px] top-1 flex h-[20px] w-[20px] items-center justify-center rounded-full border-[2px] border-black bg-white">
                    <MapPin size={12} className="text-black" />
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="mb-1 inline-block w-fit rounded-[6px] border border-[#3B5B8E] bg-white px-2 py-0.5 text-[12px] font-medium text-black shadow-sm">
                      Return
                    </span>

                    <span className="font-host text-[15px] text-[#555555]">
                      {formatBookingDate(booking.returnDateTime)}{' '}
                      {formatBookingTime(booking.returnDateTime)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column (Details Table) */}
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Customer Name
              </span>
              <span className="font-host text-[15px] font-medium text-black">
                {booking.customer.name || '-'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Email
              </span>
              <span className="font-host text-[15px] font-medium text-black">
                {booking.customer.email || '-'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Phone
              </span>
              <span className="font-host text-[15px] font-medium text-black">
                {booking.customerPhone || '-'}
              </span>
            </div>

            <div className="mt-2 flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Vehicle
              </span>
              <span className="font-host text-[15px] font-medium text-black">
                {booking.vehicle || '-'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Source Name
              </span>
              <span className="font-host text-[15px] font-medium text-black">
                {booking.sourceName || '-'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Payment
              </span>

              <span className="rounded-full bg-[#B5D57B] px-3 py-0.5 font-host text-[14px] font-medium text-black">
                {booking.payment === 'paid' ? 'Paid' : 'Unpaid'}
              </span>
            </div>

            {/* Driver section */}
            <div className="my-2 h-px w-full bg-[#E0E0E0]" />

            <div className="flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Driver
              </span>

              <span className="font-host text-[15px] font-medium text-black">
                {booking.driverAssigned
                  ? booking.driverName || 'Assigned'
                  : 'Unassigned'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-host text-[15px] font-medium text-[#757575]">
                Driver Amount
              </span>

              <span className="font-host text-[15px] font-medium text-black">
                {booking.driverAssigned
                  ? formatEuro(booking.driverAmount ?? 0)
                  : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
